import { useEffect, useState, useRef, useCallback } from 'react';
import { printError } from '@portal-internet/core';
import { setOptimizeImageSpecs, isEditModeActive } from '@portal-internet/bff';
import clsx from 'clsx';
import __find from 'lodash/find';
import __isEmpty from 'lodash/isEmpty';
import __map from 'lodash/map';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { cropPredef, sizesPredef, tipusPredef } from './constants';

setOptimizeImageSpecs(tipusPredef);

export const tipusImatgePredef = tipusPredef;

export const getCrop = (imatge, tipus, crop) => {
    if (imatge.includes('img.ccma.cat') || imatge.includes('img.3cat.cat')) {
        crop = crop || cropPredef[tipus] || '320x466'; //definim un default, aixi sempre hi ha crop
        let splitimatge = imatge?.split('.');
        let extensio = '.' + splitimatge[splitimatge.length - 1]; // .jpg
        return imatge.replace(extensio, '_' + crop + extensio);
    } else {
        return imatge;
    }
};

export const getPeu = (imatges, tipus, font) => {
    if (__isEmpty(imatges)) return false;
    if (!Array.isArray(imatges)) imatges = imatges.imatge;

    let peu = '';
    try {
        const mides = tipus && tipusPredef[tipus] ? tipusPredef[tipus] : tipusPredef['web_default'];
        const candidat = __map(mides, (mida) =>
            __find(imatges, (img) => (__isEmpty(mida.rel_name) || img.rel_name === mida.rel_name) && img.mida === mida.mida)
        ).filter((img) => img);

        if (!__isEmpty(candidat)) {
            if (!__isEmpty(candidat[0]?.peu)) {
                peu += candidat[0]?.peu + ' ';
            }
            if (!__isEmpty(candidat[0]?.font) && font !== false) {
                peu += '(' + candidat[0]?.font + ')';
            }
        }
    } catch (error) {
        printError(error, 'Imatge.jsx > getImage', error.stack);
    }

    return peu;
};

export const hasImatge = (imatges, tipus, recurs, crop, sizes = false) => {
    return !__isEmpty(getImatge(imatges, tipus, recurs, crop, sizes)?.src);
};

export const getImatge = (imatges, tipus, recurs, crop, sizes = false) => {
    if (__isEmpty(imatges)) return false;
    if (!Array.isArray(imatges)) imatges = imatges.imatge;

    let src = false;
    let alt = '';
    try {
        const mides = tipus && tipusPredef[tipus] ? tipusPredef[tipus] : tipusPredef['web_default'];
        const candidat = __map(mides, (mida) =>
            __find(imatges, (img) => (__isEmpty(mida.rel_name) || img.rel_name === mida.rel_name) && img.mida === mida.mida)
        ).filter((img) => img);

        if (!__isEmpty(candidat)) {
            src = sizes ? candidat[0].text : getCrop(candidat[0].text, tipus, crop);
            alt = candidat[0].alt || '';
        } else {
            src = recurs || null;
        }
    } catch (error) {
        printError(error, 'Imatge.jsx > getImage', error.stack);
    }

    return { src, alt };
};

const getSizes = (tipus, numColumnsRow) => {
    let sizes = false;
    if (tipus && sizesPredef[tipus]?.[numColumnsRow]) {
        sizes = sizesPredef[tipus][numColumnsRow];
    } else if (tipus && sizesPredef[tipus]?.default) {
        sizes = sizesPredef[tipus].default;
    }
    return sizes;
};

export default function Imatge(props) {
    let {
        alt,
        width,
        height,
        imatges,
        src,
        recurs,
        mostrarRecurs = true,
        layout,
        objectFit,
        objectPosition,
        className,
        tipus,
        crop,
        priority = false, //true - Prioritza la imatge per sobre de les altres, aplica automaticament loading='eager' || false - Fa cas del tipus de loading que li passem
        aspectRatio,
        fontSize, //no es pot posar per estils?
        isMosaicScroll,
        data_testid,
        handleErrorImatge,
        mostrarRecursTitol = true,
        checkisLoaded,
        noMostrarFinsCarregat,
        numColumnsRow
    } = props;

    const sizes = getSizes(tipus, numColumnsRow);
    const router = useRouter();
    const isEditMode = isEditModeActive(router?.query);

    // Si no reps loading per props, assigna el valor segons isEditMode
    let loading;
    if (props.loading !== undefined) {
        loading = props.loading;
    } else if (isEditMode) {
        loading = 'eager';
    } else {
        loading = 'lazy';
    }

    let imatge = getImatge(imatges, tipus, recurs, crop, sizes);
    if (__isEmpty(src)) {
        src = imatge.src;
    }
    if (alt === undefined) {
        if (imatge.alt === undefined) {
            alt = '';
        } else {
            alt = imatge.alt;
        }
    }

    const [isImageLoading, setImageLoading] = useState(true);
    const isMountedRef = useRef(true);

    const recursWidth = !width || isMosaicScroll ? '100%' : width + 'px';
    const recursHeight = !height || isMosaicScroll ? '100%' : height + 'px';

    const customLoader = useCallback(({ src, width }) => {
        const cleanSrc = src?.trimEnd();
        if (cleanSrc?.includes('img.ccma.cat') || cleanSrc?.includes('img.3cat.cat')) {
            const srcClean = cleanSrc.replace(/[_].*[.]/, '');
            const splitimatge = srcClean?.split('.');
            const extensio = '.' + splitimatge[splitimatge.length - 1];
            return cleanSrc.replace(extensio, '_' + width + extensio);
        } else {
            return cleanSrc;
        }
    }, []);

    const onLoadImage = useCallback(() => {
        if (isMountedRef.current) {
            setImageLoading(false);
            if (checkisLoaded) {
                checkisLoaded();
            }
        }
    }, [checkisLoaded]);

    const onErrorImage = useCallback(
        (e) => {
            if (handleErrorImatge) handleErrorImatge(e);
            else {
                e.target.style.display = 'none';
            }
        },
        [handleErrorImatge]
    );

    const styleImatge = clsx({
        [className]: !__isEmpty(className),
        'no-imatge': !src
    });

    const titolRecurs =
        alt && mostrarRecursTitol && !isImageLoading ? (
            <span
                style={{
                    textAlign: 'center',
                    lineHeight: '1.5',
                    fontSize: fontSize //no es pot posar per estils?
                }}
            >
                {alt}
            </span>
        ) : (
            <span></span>
        );

    const tipusImatgeDefault = ['web_destacat', 'web_destacat_horitzontal', 'web_slider'];

    const contentImageDefault = () =>
        tipusImatgeDefault.includes(tipus) && !isImageLoading ? (
            <Image
                className={styleImatge}
                src={'https://statics.3cat.cat/recursos/3catinfo/3catinfo_imatge_per_defecte.png'}
                unoptimized={true}
                alt={alt}
                objectPosition={objectPosition}
                priority={priority}
                loading={priority ? 'eager' : loading}
                onLoad={onLoadImage}
                onError={onErrorImage}
                data-testid={data_testid}
                width={width}
                height={height}
                {...(layout === 'fill' && { fill: true })}
                {...(!__isEmpty(objectFit) && { style: { objectFit: objectFit } })}
            />
        ) : (
            <div
                className={styleImatge}
                style={{
                    position: 'absolute',
                    width: recursWidth,
                    height: recursHeight,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    color: '#E5E5E5',
                    aspectRatio: aspectRatio,
                    zIndex: 1
                }}
            >
                {titolRecurs}
            </div>
        );

    const convertHttps = (urlImatge) => {
        const regex = /^http:\/\//i;
        return urlImatge.replace(regex, 'https://');
    };

    useEffect(() => {
        isMountedRef.current = true;
        if (!src) {
            onLoadImage();
        }
        return () => {
            isMountedRef.current = false; // Això evita l'actualització de l'estat
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [src, onLoadImage]);

    if (src) {
        src = convertHttps(src);
    }

    const renderImage = () => {
        if (src) {
            return (
                <>
                    {!noMostrarFinsCarregat && isImageLoading && mostrarRecurs && contentImageDefault()}
                    {sizes ? (
                        <Image
                            className={styleImatge}
                            loader={customLoader}
                            src={src?.trimEnd()}
                            alt={alt}
                            objectPosition={objectPosition}
                            priority={priority}
                            loading={priority ? 'eager' : loading}
                            onLoad={onLoadImage}
                            onError={onErrorImage}
                            data-testid={data_testid}
                            sizes={sizes}
                            width={width}
                            height={height}
                            {...(layout === 'fill' && { fill: true })}
                            {...(!__isEmpty(objectFit) && { style: { objectFit: objectFit } })}
                        />
                    ) : (
                        <Image
                            className={styleImatge}
                            src={src?.trimEnd()}
                            unoptimized={true}
                            alt={alt}
                            objectPosition={objectPosition}
                            priority={priority}
                            loading={priority ? 'eager' : loading}
                            onLoad={onLoadImage}
                            onError={onErrorImage}
                            data-testid={data_testid}
                            width={width}
                            height={height}
                            {...(layout === 'fill' && { fill: true })}
                            {...(!__isEmpty(objectFit) && { style: { objectFit: objectFit } })}
                        />
                    )}
                </>
            );
        }

        if (!noMostrarFinsCarregat && !isImageLoading && props.children) {
            return <>{props.children}</>;
        }

        if (!noMostrarFinsCarregat && !isImageLoading && mostrarRecurs) {
            return <>{contentImageDefault()}</>;
        }

        return null;
    };

    return renderImage();
}
